import { useEffect, useMemo, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Check, Sparkles } from 'lucide-react'
import type { Currency, Entry, EntrySnapshot, EntryType } from '@/types/domain'
import { defaultConverter } from '@/core/calculation/converter'
import { useAutocomplete } from '@/hooks/useAutocomplete'
import type { EntryFormValues } from '@/hooks/useEntryMutations'
import { useSettingsStore } from '@/core/storage/zustand-store'
import { formatBRL, formatOrig, formatStylizedDate } from '../format'
import { Button } from '../shadcn/button'
import { Input } from '../shadcn/input'
import { Label } from '../shadcn/label'
import { Separator } from '../shadcn/separator'
import { SegmentedDateInput } from './SegmentedDateInput'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '../shadcn/sheet'

const entryFormSchema = z.object({
  tipo: z.enum(['rendimento', 'imposto_pago_exterior']),
  dataRecebimento: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'use o seletor de data'),
  moedaOrigem: z.enum(['USD', 'CAD']),
  valorOriginal: z.coerce.number().positive('valor deve ser maior que zero'),
  pagador: z.string().optional(),
  categoria: z.string().optional(),
  tag: z.string().optional(),
  descricao: z.string().optional(),
})

function buildDefaults(defaultCurrency: 'USD' | 'CAD'): EntryFormValues {
  return {
    tipo: 'rendimento',
    dataRecebimento: new Date().toISOString().slice(0, 10),
    moedaOrigem: defaultCurrency,
    valorOriginal: 0,
    pagador: '',
    categoria: '',
    tag: '',
    descricao: '',
  }
}

export interface EntryDrawerProps {
  open: boolean
  /** Edit mode: submit closes the drawer, title says "Editar". */
  entry?: Entry | null
  /** Create-from-template mode: drawer pre-fills with the template's values
   *  but acts as create (submit keeps drawer open). Ignored when `entry` is
   *  set. Powers the "Copiar" action on the listing row. */
  template?: Entry | null
  onClose: () => void
  onSubmit: (values: EntryFormValues) => void
  converter?: { convert: typeof defaultConverter.convert }
}

type PreviewState =
  | { status: 'idle' }
  | { status: 'computing' }
  | { status: 'ready'; snapshot: EntrySnapshot }
  | { status: 'error'; message: string }

function defaultsFromEntry(source: Entry): EntryFormValues {
  return {
    tipo: source.tipo,
    dataRecebimento: source.dataRecebimento,
    moedaOrigem: source.moedaOrigem,
    valorOriginal: source.valorOriginal,
    pagador: source.pagador ?? '',
    categoria: source.categoria ?? '',
    tag: source.tag ?? '',
    descricao: source.descricao ?? '',
  }
}

export function EntryDrawer({
  open,
  entry,
  template,
  onClose,
  onSubmit,
  converter,
}: EntryDrawerProps) {
  const defaultCurrency = useSettingsStore((s) => s.settings.defaultCurrency)
  const defaults: EntryFormValues = useMemo(() => {
    if (entry) return defaultsFromEntry(entry)
    if (template) return defaultsFromEntry(template)
    return buildDefaults(defaultCurrency)
  }, [entry, template, defaultCurrency])

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
    watch,
  } = useForm<EntryFormValues>({
    resolver: zodResolver(entryFormSchema),
    defaultValues: defaults,
  })

  const dateInputRef = useRef<HTMLInputElement | null>(null)
  const focusDate = (): void => {
    // Defer one tick so the field is mounted after a reset / sheet open.
    window.setTimeout(() => dateInputRef.current?.focus(), 50)
  }

  const [justSaved, setJustSaved] = useState(false)

  useEffect(() => {
    if (!open) return
    reset(defaults)
    // Slightly longer delay on open to ride out the sheet slide animation.
    const t = window.setTimeout(() => dateInputRef.current?.focus(), 150)
    return () => window.clearTimeout(t)
  }, [open, defaults, reset])

  const autocomplete = useAutocomplete()
  const watched = watch()
  const previewSnapshot = useLivePreview(watched, open, converter)

  const submit = handleSubmit((values) => {
    onSubmit(values)
    if (entry) {
      onClose()
      return
    }
    // Create mode: stay open. Preserve dataRecebimento + moedaOrigem (and
    // metadata so repeated entries from the same source/category don't
    // re-type those fields). Clear only valorOriginal and refocus the date
    // input so the next entry's flow starts there.
    reset({ ...values, valorOriginal: 0 })
    setJustSaved(true)
    window.setTimeout(() => setJustSaved(false), 1500)
    focusDate()
  })

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <SheetContent className="flex w-full flex-col p-0 sm:max-w-lg">
        <SheetHeader className="border-b border-border px-6 py-4">
          <SheetTitle className="font-display text-2xl font-normal tracking-tight">
            {entry ? 'Editar lançamento' : template ? 'Novo lançamento (cópia)' : 'Novo lançamento'}
          </SheetTitle>
          <SheetDescription>
            Os valores são convertidos automaticamente conforme a IN SRF 208/2002.
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={submit} className="flex flex-1 flex-col overflow-y-auto">
          <div className="space-y-5 px-6 py-5">
            <Field label="Moeda">
              <Controller
                control={control}
                name="moedaOrigem"
                render={({ field }) => (
                  <div className="flex gap-2">
                    <ChipOption
                      label="USD"
                      checked={field.value === 'USD'}
                      onChange={() => field.onChange('USD' as Currency)}
                    />
                    <ChipOption
                      label="CAD"
                      checked={field.value === 'CAD'}
                      onChange={() => field.onChange('CAD' as Currency)}
                    />
                  </div>
                )}
              />
            </Field>

            <Field label="Tipo">
              <Controller
                control={control}
                name="tipo"
                render={({ field }) => (
                  <div className="flex gap-2">
                    <ChipOption
                      label="Rendimento"
                      checked={field.value === 'rendimento'}
                      onChange={() => field.onChange('rendimento' as EntryType)}
                    />
                    <ChipOption
                      label="Imposto pago no exterior"
                      checked={field.value === 'imposto_pago_exterior'}
                      onChange={() => field.onChange('imposto_pago_exterior' as EntryType)}
                    />
                  </div>
                )}
              />
            </Field>

            <Field label="Data do recebimento" error={errors.dataRecebimento?.message} htmlFor="dataRecebimento">
              <Controller
                control={control}
                name="dataRecebimento"
                render={({ field }) => (
                  <SegmentedDateInput
                    id="dataRecebimento"
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    ref={(el) => {
                      field.ref(el)
                      dateInputRef.current = el
                    }}
                  />
                )}
              />
            </Field>

            <Field label="Valor original" error={errors.valorOriginal?.message} htmlFor="valorOriginal">
              <Input
                id="valorOriginal"
                type="number"
                step="0.0001"
                min="0"
                className="font-mono"
                {...register('valorOriginal')}
              />
            </Field>

            <Field label="Pagador" htmlFor="pagador">
              <Input
                id="pagador"
                list="pagador-options"
                {...register('pagador')}
              />
              <datalist id="pagador-options">
                {autocomplete.pagadores.map((p) => (
                  <option key={p} value={p} />
                ))}
              </datalist>
            </Field>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Categoria" htmlFor="categoria">
                <Input id="categoria" list="categoria-options" {...register('categoria')} />
                <datalist id="categoria-options">
                  {autocomplete.categorias.map((c) => (
                    <option key={c} value={c} />
                  ))}
                </datalist>
              </Field>
              <Field label="Tag" htmlFor="tag">
                <Input id="tag" list="tag-options" {...register('tag')} />
                <datalist id="tag-options">
                  {autocomplete.tags.map((t) => (
                    <option key={t} value={t} />
                  ))}
                </datalist>
              </Field>
            </div>

            <Field label="Descrição (opcional)" htmlFor="descricao">
              <textarea
                id="descricao"
                rows={2}
                {...register('descricao')}
                className="border-input bg-transparent placeholder:text-muted-foreground focus-visible:ring-ring flex w-full rounded-md border px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
              />
            </Field>

            <Separator />

            <PreviewBox preview={previewSnapshot} />
          </div>

          <SheetFooter className="border-t border-border bg-muted/30 px-6 py-4">
            <Button type="button" variant="ghost" onClick={onClose}>
              {entry ? 'Cancelar' : 'Concluir'}
            </Button>
            <Button type="submit" variant={justSaved ? 'success' : 'default'}>
              {justSaved ? (
                <>
                  <Check className="h-4 w-4" />
                  Salvo · próximo
                </>
              ) : entry ? (
                'Salvar alterações'
              ) : (
                'Salvar lançamento'
              )}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  )
}

function Field({
  label,
  error,
  htmlFor,
  children,
}: {
  label: string
  error?: string
  htmlFor?: string
  children: React.ReactNode
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor} className="text-muted-foreground text-[11px] font-medium tracking-widest2 uppercase">
        {label}
      </Label>
      {children}
      {error ? <p className="text-destructive text-[12px]">{error}</p> : null}
    </div>
  )
}

function ChipOption({
  label,
  checked,
  onChange,
}: {
  label: string
  checked: boolean
  onChange: () => void
}) {
  return (
    <button
      type="button"
      onClick={onChange}
      aria-pressed={checked}
      className={[
        'flex h-9 items-center justify-center rounded-md border px-3 text-sm font-medium transition-colors',
        checked
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-input bg-background hover:bg-muted',
      ].join(' ')}
    >
      {label}
    </button>
  )
}

function PreviewBox({ preview }: { preview: PreviewState }) {
  return (
    <div className="bg-muted/40 rounded-md border border-border p-4">
      <div className="text-muted-foreground mb-2 flex items-center gap-2 text-[10px] font-medium tracking-widest2 uppercase">
        <Sparkles className="h-3 w-3" />
        Prévia da conversão
      </div>
      {preview.status === 'idle' ? (
        <p className="text-muted-foreground text-sm">
          Preencha data, moeda e valor para calcular automaticamente.
        </p>
      ) : preview.status === 'computing' ? (
        <p className="text-muted-foreground text-sm animate-pulse">Calculando…</p>
      ) : preview.status === 'error' ? (
        <p className="text-warning text-sm">{preview.message}</p>
      ) : (
        <PreviewResult snapshot={preview.snapshot} />
      )}
    </div>
  )
}

function PreviewResult({ snapshot }: { snapshot: EntrySnapshot }) {
  if (snapshot.status !== 'calculado') {
    const detail = snapshot.erro?.detalhe ?? 'sem cotação disponível'
    return <p className="text-warning text-sm">{detail}</p>
  }
  return (
    <div className="space-y-2 text-sm">
      <div className="flex items-baseline justify-between">
        <span className="text-muted-foreground text-[12px]">USD</span>
        <span className="font-mono text-[14px]">{formatOrig(snapshot.valorUSD)}</span>
      </div>
      <div className="flex items-baseline justify-between">
        <span className="text-muted-foreground text-[12px]">BRL</span>
        <span className="font-display text-2xl">R$ {formatBRL(snapshot.valorBRL)}</span>
      </div>
      <Separator />
      <p className="text-muted-foreground text-[11px]">
        PTAX compra de&nbsp;
        <span className="text-foreground font-mono">
          {snapshot.dataPTAXAplicavel ? formatStylizedDate(snapshot.dataPTAXAplicavel) : '—'}
        </span>
        <br />
        BCB SGS 10813
        {snapshot.fontes?.boc
          ? ` · BoC FXUSDCAD ${formatStylizedDate(snapshot.fontes.boc.data)}`
          : ''}
      </p>
    </div>
  )
}

function useLivePreview(
  values: EntryFormValues,
  open: boolean,
  converter?: { convert: typeof defaultConverter.convert },
): PreviewState {
  const [state, setState] = useState<PreviewState>({ status: 'idle' })

  useEffect(() => {
    if (!open) {
      setState({ status: 'idle' })
      return
    }
    if (
      !values.dataRecebimento ||
      !/^\d{4}-\d{2}-\d{2}$/.test(values.dataRecebimento) ||
      !Number.isFinite(values.valorOriginal) ||
      values.valorOriginal <= 0
    ) {
      setState({ status: 'idle' })
      return
    }

    const conv = converter ?? defaultConverter
    let cancelled = false
    setState({ status: 'computing' })
    const handle = window.setTimeout(() => {
      conv
        .convert({
          moedaOrigem: values.moedaOrigem,
          valorOriginal: values.valorOriginal,
          dataRecebimento: values.dataRecebimento,
        })
        .then((snapshot) => {
          if (!cancelled) setState({ status: 'ready', snapshot })
        })
        .catch((err: unknown) => {
          if (!cancelled) {
            setState({
              status: 'error',
              message: err instanceof Error ? err.message : 'erro desconhecido',
            })
          }
        })
    }, 600)

    return () => {
      cancelled = true
      window.clearTimeout(handle)
    }
  }, [open, values.dataRecebimento, values.moedaOrigem, values.valorOriginal, converter])

  return state
}
